import React, { useState } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { GripVertical, Pencil, Plus, Trash2, ImageIcon, Move } from "lucide-react";
import type { PriorityTile } from "./PriorityTiles";

interface SortableTileProps {
  tile: PriorityTile;
  onEdit: () => void;
  onDelete: () => void;
}

function SortableTile({ tile, onEdit, onDelete }: SortableTileProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tile.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const colSpan = tile.colSpan || 3;
  const rowSpan = tile.rowSpan || 2;
  const posX = tile.imagePositionX ?? 50;
  const posY = tile.imagePositionY ?? 50;
  const scale = tile.imageScale ?? 100;

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        gridColumn: `span ${colSpan}`,
        gridRow: `span ${rowSpan}`,
      }}
      className={cn(
        "relative border-2 border-dashed border-primary/40 bg-white overflow-hidden group",
        isDragging && "z-50 opacity-70 shadow-xl"
      )}
    >
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="absolute top-2 left-2 z-10 p-1 bg-background/80 rounded cursor-grab active:cursor-grabbing"
      >
        <GripVertical className="h-4 w-4 text-muted-foreground" />
      </div>

      {/* Edit/Delete Controls */}
      <div className="absolute top-2 right-2 z-10 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button variant="secondary" size="icon" className="h-7 w-7" onClick={onEdit}>
          <Pencil className="h-3 w-3" />
        </Button>
        <Button variant="destructive" size="icon" className="h-7 w-7" onClick={onDelete}>
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>

      {/* Image or Placeholder */}
      {tile.image ? (
        <img
          src={tile.image}
          alt={tile.label}
          className="w-full h-full"
          style={{
            objectFit: 'cover',
            objectPosition: `${posX}% ${posY}%`,
            transform: `scale(${scale / 100})`,
            transformOrigin: `${posX}% ${posY}%`,
          }}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-muted/50">
          <ImageIcon className="h-12 w-12 text-muted-foreground/30" />
        </div>
      )}

      {/* Label Bar */}
      <div className={cn(
        "absolute left-0 right-0 bg-primary text-primary-foreground px-3 py-2 font-semibold text-sm",
        rowSpan > 2 ? "bottom-0" : "top-0"
      )}>
        {tile.label || "Titel eingeben..."}
      </div>

      {/* Size Badge */}
      <div className="absolute bottom-2 left-2 bg-background/80 text-xs px-2 py-0.5 rounded flex items-center gap-1">
        <Move className="h-3 w-3" />
        {colSpan}×{rowSpan}
      </div>
    </div>
  );
}

interface TileEditDialogProps {
  tile: PriorityTile | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (tile: PriorityTile) => void;
}

function TileEditDialog({ tile, open, onOpenChange, onSave }: TileEditDialogProps) {
  const [label, setLabel] = useState(tile?.label || "");
  const [image, setImage] = useState(tile?.image || "");
  const [colSpan, setColSpan] = useState(tile?.colSpan || 3);
  const [rowSpan, setRowSpan] = useState(tile?.rowSpan || 2);
  const [positionX, setPositionX] = useState(tile?.imagePositionX ?? 50);
  const [positionY, setPositionY] = useState(tile?.imagePositionY ?? 50);
  const [imageScale, setImageScale] = useState(tile?.imageScale ?? 100);
  const [titlePosition, setTitlePosition] = useState<'top' | 'bottom'>(tile?.titlePosition || 'top');
  const [checkboxPosition, setCheckboxPosition] = useState<'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'>(
    tile?.checkboxPosition || 'bottom-right'
  );

  React.useEffect(() => {
    if (tile) {
      setLabel(tile.label);
      setImage(tile.image || "");
      setColSpan(tile.colSpan || 3);
      setRowSpan(tile.rowSpan || 2);
      setPositionX(tile.imagePositionX ?? 50);
      setPositionY(tile.imagePositionY ?? 50);
      setImageScale(tile.imageScale ?? 100);
      setTitlePosition(tile.titlePosition || 'top');
      setCheckboxPosition(tile.checkboxPosition || 'bottom-right');
    }
  }, [tile]);

  const handleSave = () => {
    if (!tile) return;
    onSave({
      ...tile,
      label,
      image: image || undefined,
      colSpan,
      rowSpan,
      imagePositionX: positionX,
      imagePositionY: positionY,
      imageScale,
      titlePosition,
      checkboxPosition,
    });
    onOpenChange(false);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      setImage(event.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl flex flex-col max-h-[90vh]">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Kachel bearbeiten</DialogTitle>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-y-auto space-y-6 pt-4 pr-1">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="tile-label">Titel</Label>
            <Input
              id="tile-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="z.B. Eigentum schaffen"
            />
          </div>

          {/* Grid Size */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Spalten (1-12)</Label>
              <Select value={colSpan.toString()} onValueChange={(v) => setColSpan(parseInt(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => (
                    <SelectItem key={n} value={n.toString()}>
                      {n} {n === 1 ? 'Spalte' : 'Spalten'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Zeilen (1-12)</Label>
              <Select value={rowSpan.toString()} onValueChange={(v) => setRowSpan(parseInt(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => (
                    <SelectItem key={n} value={n.toString()}>
                      {n} {n === 1 ? 'Zeile' : 'Zeilen'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Title Position & Checkbox Position */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Titel-Position</Label>
              <Select value={titlePosition} onValueChange={(v) => setTitlePosition(v as 'top' | 'bottom')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="top">Oben</SelectItem>
                  <SelectItem value="bottom">Unten</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Checkbox-Position</Label>
              <Select value={checkboxPosition} onValueChange={(v) => setCheckboxPosition(v as any)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="top-left">Oben links</SelectItem>
                  <SelectItem value="top-right">Oben rechts</SelectItem>
                  <SelectItem value="bottom-left">Unten links</SelectItem>
                  <SelectItem value="bottom-right">Unten rechts</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Image Upload */}
          <div className="space-y-2">
            <Label>Bild</Label>
            <div className="flex gap-2">
              <Input
                placeholder="Bild-URL eingeben..."
                value={image}
                onChange={(e) => setImage(e.target.value)}
                className="flex-1"
              />
              <Label htmlFor="tile-image-upload" className="cursor-pointer">
                <div className="h-10 px-4 bg-secondary hover:bg-secondary/80 rounded-md flex items-center gap-2 text-sm font-medium">
                  <ImageIcon className="h-4 w-4" />
                  Upload
                </div>
                <input
                  id="tile-image-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpload}
                />
              </Label>
            </div>
          </div>

          {/* Image Preview with Controls */}
          {image && (
            <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
              <Label className="text-base font-semibold">Bildvorschau & Anpassung</Label>
              
              {/* Preview - single image with prominent frame showing tile boundaries */}
              <div className="relative bg-muted rounded-lg p-4 flex items-center justify-center">
                <div 
                  className="relative border-4 border-primary overflow-hidden"
                  style={{ 
                    aspectRatio: `${colSpan} / ${rowSpan}`,
                    height: '280px',
                    width: 'auto',
                  }}
                >
                  <img
                    src={image}
                    alt="Preview"
                    className="absolute"
                    style={{
                      width: `${imageScale}%`,
                      height: `${imageScale}%`,
                      objectFit: 'cover',
                      left: `${50 - (positionX * imageScale) / 100}%`,
                      top: `${50 - (positionY * imageScale) / 100}%`,
                      transform: 'translate(-50%, -50%)',
                    }}
                  />
                </div>
                
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2 h-6 w-6 z-10"
                  onClick={() => setImage("")}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
              
              {/* Size indicator */}
              <p className="text-xs text-muted-foreground text-center">
                Kachelgröße: {colSpan} × {rowSpan} — Der orange Rahmen zeigt den sichtbaren Kachelbereich
              </p>

              {/* Position X */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <Label>Position X</Label>
                  <span className="text-muted-foreground">{positionX}%</span>
                </div>
                <Slider
                  value={[positionX]}
                  onValueChange={([v]) => setPositionX(v)}
                  min={0}
                  max={100}
                  step={1}
                />
              </div>

              {/* Position Y */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <Label>Position Y</Label>
                  <span className="text-muted-foreground">{positionY}%</span>
                </div>
                <Slider
                  value={[positionY]}
                  onValueChange={([v]) => setPositionY(v)}
                  min={0}
                  max={100}
                  step={1}
                />
              </div>

              {/* Zoom/Scale */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <Label>Zoom</Label>
                  <span className="text-muted-foreground">{imageScale}%</span>
                </div>
                <Slider
                  value={[imageScale]}
                  onValueChange={([v]) => setImageScale(v)}
                  min={100}
                  max={200}
                  step={5}
                />
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSave}>
              Speichern
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface PriorityTilesEditorProps {
  tiles: PriorityTile[];
  onChange: (tiles: PriorityTile[]) => void;
  title?: string;
  subtitle?: string;
  onTitleChange?: (title: string) => void;
  onSubtitleChange?: (subtitle: string) => void;
}

export function PriorityTilesEditor({
  tiles,
  onChange,
  title = "Welche Bereiche sind Ihnen wichtig?",
  subtitle = "Ihr maßgeschneidertes Konzept.",
  onTitleChange,
  onSubtitleChange,
}: PriorityTilesEditorProps) {
  const [editingTile, setEditingTile] = useState<PriorityTile | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = tiles.findIndex((t) => t.id === active.id);
    const newIndex = tiles.findIndex((t) => t.id === over.id);

    const newTiles = [...tiles];
    const [removed] = newTiles.splice(oldIndex, 1);
    newTiles.splice(newIndex, 0, removed);

    onChange(newTiles);
  };

  const handleEdit = (tile: PriorityTile) => {
    setEditingTile(tile);
    setEditDialogOpen(true);
  };

  const handleSave = (updatedTile: PriorityTile) => {
    onChange(tiles.map((t) => (t.id === updatedTile.id ? updatedTile : t)));
  };

  const handleDelete = (tileId: string) => {
    onChange(tiles.filter((t) => t.id !== tileId));
  };

  const handleAddTile = () => {
    const newTile: PriorityTile = {
      id: `tile-${Date.now()}`,
      label: "Neue Kachel",
      colSpan: 3,
      rowSpan: 2,
    };
    onChange([...tiles, newTile]);
    setEditingTile(newTile);
    setEditDialogOpen(true);
  };

  return (
    <div className="space-y-6 pt-8">
      {/* Editable Title/Subtitle */}
      <div className="space-y-2">
        <Input
          value={title}
          onChange={(e) => onTitleChange?.(e.target.value)}
          placeholder="Titel eingeben..."
          className="text-2xl md:text-3xl font-bold border-dashed text-left h-auto py-2"
        />
        <Input
          value={subtitle}
          onChange={(e) => onSubtitleChange?.(e.target.value)}
          placeholder="Untertitel eingeben..."
          className="text-lg italic text-muted-foreground border-dashed text-left h-auto py-1"
        />
      </div>

      {/* Tiles Grid with DnD - 12 column grid */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={tiles.map((t) => t.id)} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-12 gap-3 auto-rows-[80px]">
            {tiles.map((tile) => (
              <SortableTile
                key={tile.id}
                tile={tile}
                onEdit={() => handleEdit(tile)}
                onDelete={() => handleDelete(tile.id)}
              />
            ))}
            
            {/* Add Tile Button */}
            <button
              onClick={handleAddTile}
              className="col-span-3 row-span-2 border-2 border-dashed border-muted-foreground/30 rounded-lg flex flex-col items-center justify-center gap-2 hover:border-primary/50 hover:bg-muted/30 transition-colors"
            >
              <Plus className="h-8 w-8 text-muted-foreground/50" />
              <span className="text-sm text-muted-foreground">Kachel hinzufügen</span>
            </button>
          </div>
        </SortableContext>
      </DndContext>

      {/* Edit Dialog */}
      <TileEditDialog
        tile={editingTile}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        onSave={handleSave}
      />
    </div>
  );
}
